import { z } from "zod";

/**
 * In-band agent action protocol (D-13): the tutor stream may end with a
 * `[ACTION{...json}]` token proposing a board action. Mirrors the
 * `endToken.ts` `[CONVERSATION_END]` precedent - the LLM can only emit text;
 * nothing mutates until the learner taps the confirm chip, whose POST hits
 * the same authenticated board routes the on-page buttons use (D-14).
 */
const actionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("regenerate_slot"),
    modality: z.enum(["READING", "WRITING", "SPEAKING", "LISTENING"]),
  }),
  z.object({
    type: z.literal("create_subject_lesson"),
    modality: z.enum(["READING", "WRITING", "SPEAKING", "LISTENING"]),
    subject: z.string().min(1).max(200),
  }),
]);

export type AgentAction = z.infer<typeof actionSchema>;

// [\s\S] instead of the `s` (dotAll) flag: container tsc targets pre-es2018.
const TOKEN_RE = /\[ACTION(\{[\s\S]*?\})\]\s*$/;

/**
 * Strip a trailing action token from tutor text. Malformed JSON, unknown
 * types, or invalid payloads drop silently - the tip text still renders,
 * no chip (research A4).
 */
export function parseActionToken(text: string): { display: string; action: AgentAction | null } {
  const m = text.match(TOKEN_RE);
  if (!m) return { display: text, action: null };
  const display = text.slice(0, m.index).trimEnd();
  try {
    const parsed = actionSchema.safeParse(JSON.parse(m[1]));
    return { display, action: parsed.success ? parsed.data : null }; // malformed → drop silently
  } catch {
    return { display, action: null };
  }
}

/**
 * System-prompt block teaching the tutor the two available board actions and
 * the emission format. `skillLevelsLine` is the per-modality levels line
 * (same format as the tutor-context "Skill levels:" line).
 */
export function buildActionInstructions(skillLevelsLine: string): string {
  return [
    "Board actions you can propose:",
    "The learner has a lesson board with one active lesson per skill (READING, WRITING, SPEAKING, LISTENING).",
    skillLevelsLine ? `Current skill levels: ${skillLevelsLine}` : null,
    "Available actions:",
    "- regenerate_slot: regenerate the learner's lesson for a modality at their current level.",
    "- create_subject_lesson: create a lesson about a specific subject for a modality.",
    "To propose an action, end your reply with the bracketed token on its own line.",
    "Propose at most ONE action, and only when the learner asks for it or it clearly helps; otherwise reply normally with no token.",
    "Examples:",
    '[ACTION{"type":"regenerate_slot","modality":"WRITING"}]',
    '[ACTION{"type":"create_subject_lesson","modality":"READING","subject":"ordering food at a restaurant"}]',
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}
